import posthog from "posthog-js";

import { env } from "@/config/env";
import { useAuthStore } from "@/stores/auth.store";

/**
 * Mesure d'usage du back-office.
 *
 * À quoi cela sert : savoir quels écrans les opérateurs utilisent réellement,
 * où ils s'arrêtent, quelles fonctions coûtent trois clics de trop. Un
 * back-office s'enrichit vite d'écrans que plus personne n'ouvre.
 *
 * DIFFÉRENCES ASSUMÉES AVEC LE STOREFRONT :
 *
 *  - Aucun enregistrement de session. Les écrans du back-office affichent en
 *    clair des adresses, des téléphones et des montants appartenant aux
 *    clients. Rejouer ces sessions reviendrait à constituer une copie de la
 *    base client dans un outil tiers — hors de question, quel que soit le
 *    masquage appliqué.
 *
 *  - Pas de capture automatique des clics. Les libellés de boutons du
 *    back-office contiennent des noms de clients et des numéros de commande ;
 *    l'autocapture les enverrait comme propriétés d'événement.
 *
 * Reste la navigation entre écrans, qui n'expose aucune donnée client.
 */
export function initPostHog(): void {
	if (!env.posthogKey) return;

	posthog.init(env.posthogKey, {
		api_host: env.posthogHost,
		// Application à navigation client : sans cela, une session entière ne
		// compterait que pour une seule vue.
		capture_pageview: "history_change",
		disable_session_recording: true,
		autocapture: false,
		// Les opérateurs sont tous identifiés : inutile de compter les profils
		// anonymes, il n'y en a pas.
		person_profiles: "always",
		// La bibliothèque va chercher sa configuration sur `/array/<clé>/config.js`
		// au démarrage. Notre instance auto-hébergée ne sert pas cette route
		// (502), ce qui n'empêche NI la capture NI les indicateurs — les
		// événements arrivent bien — mais inscrit deux erreurs rouges dans la
		// console à chaque page. On ne s'en sert pas : autant ne pas la demander.
		advanced_disable_decide: true,
		// Même raison : les modules complémentaires servis depuis
		// `/static/<version>/…` n'existent pas sur notre instance, et la réponse
		// HTML qui en tient lieu est refusée comme script par le navigateur.
		disable_external_dependency_loading: true,
	});

	// L'identité suit le store d'authentification plutôt qu'un composant :
	// connexion, expiration de session et déconnexion passent tous par là,
	// sans qu'aucun écran n'ait à s'en préoccuper.
	useAuthStore.subscribe((state, previous) => {
		if (state.user?.id === previous.user?.id) return;

		if (state.user) {
			// Le rôle sert à segmenter (un magasinier et un comptable n'ont pas
			// les mêmes parcours). Ni nom, ni e-mail : ce sont des données
			// personnelles, et l'identifiant suffit à recouper avec la base.
			posthog.identify(state.user.id, { role: state.user.role.key });
		} else {
			// Poste partagé : sans `reset()`, l'opérateur suivant hériterait de
			// l'identité du précédent.
			posthog.reset();
		}
	});

	// Le store est rechargé depuis le stockage local au démarrage : un
	// opérateur déjà connecté ne déclenche aucun changement d'état, donc aucun
	// appel de l'abonnement ci-dessus.
	const user = useAuthStore.getState().user;
	if (user) posthog.identify(user.id, { role: user.role.key });
}
